import React, { useMemo, useState } from "react";
import { ChevronDown, Layers, Lock, PauseCircle } from "lucide-react";
import { StatusMeter, TeamLogo } from "../bondhouse/FixtureStatus";
import { getFixtureScore } from "../../utils/betDetailHelpers";
import { getFixtureMarketCount } from "../../utils/bondhouseHelpers";
import FixtureCardInsight from "./FixtureCardInsight";
import {
  findSlipLeg,
  fmtOdd,
  formatKickoffShort,
  formatOddPickLabel,
  getOneXTwoPicks,
  isFixtureBettable,
  isSameOddPick,
} from "../../utils/bettingDeskHelpers";

const QuickOddBtn = ({ label, pick, selected, disabled, onPick }) => (
  <button
    type="button"
    className={`pbd-odd-btn ${selected ? "pbd-odd-btn-selected" : ""}`}
    disabled={disabled || !pick}
    onClick={() => pick && onPick?.(pick)}
  >
    <span className="pbd-odd-btn-label">{label}</span>
    <span className="pbd-odd-btn-value tabular-nums">{pick ? fmtOdd(pick.value) : "—"}</span>
  </button>
);

const FixtureProgramCard = ({
  fixture,
  slip,
  busy,
  onQuickPick,
  onOpenMarkets,
}) => {
  const bettable = isFixtureBettable(fixture);
  const isSuspended = !!fixture?.suspended;
  const oneXTwo = useMemo(() => getOneXTwoPicks(fixture), [fixture]);
  const leg = useMemo(() => findSlipLeg(slip, fixture._id), [slip, fixture._id]);
  const selectedPick = leg?.selectedOdd || null;
  const score = useMemo(() => getFixtureScore(fixture), [fixture]);

  const home = fixture.teams?.home;
  const away = fixture.teams?.away;
  const league = fixture.league?.name || "League";
  const marketCount = useMemo(() => getFixtureMarketCount(fixture), [fixture]);

  const pickBlocked = isSuspended && !leg;
  const bettorsCount = fixture.bettorsCount || 0;
  const mktsOffered = fixture.marketsOffered || marketCount;
  const [insightOpen, setInsightOpen] = useState(false);
  const hasInsight = Boolean(
    fixture?.predictionCounts ||
      fixture?.standings?.home ||
      fixture?.standings?.away
  );

  return (
    <article
      className={`pbd-fixture-card ${!bettable ? "pbd-fixture-card-locked" : ""} ${
        leg ? "pbd-fixture-card-in-slip" : ""
      } ${isSuspended ? "pbd-fixture-card-suspended" : ""}`}
    >
      {isSuspended ? (
        <div className="pbd-fixture-suspended-banner">
          <PauseCircle size={12} />
          <span>Suspended — not accepting new picks</span>
        </div>
      ) : null}

      <div className="pbd-fixture-card-top">
        <div className="pbd-fixture-meta">
          <span className="pbd-fixture-league">{league}</span>
          <span className="pbd-fixture-kickoff">{formatKickoffShort(fixture)}</span>
        </div>
        <div className="pbd-fixture-top-right">
          {leg ? <span className="pbd-fixture-slip-chip">On slip</span> : null}
          <StatusMeter fixture={fixture} variant="compact" />
        </div>
      </div>

      <div className="pbd-fixture-match">
        <div className="pbd-fixture-team">
          <TeamLogo team={home} size="sm" />
          <span className="truncate">{home?.name || "TBD"}</span>
        </div>
        <div className="pbd-fixture-centre">
          {score.showScore ? (
            <span className="pbd-fixture-score tabular-nums">
              {score.home ?? 0} – {score.away ?? 0}
            </span>
          ) : (
            <span className="pbd-fixture-vs">vs</span>
          )}
        </div>
        <div className="pbd-fixture-team pbd-fixture-team-away">
          <TeamLogo team={away} size="sm" />
          <span className="truncate">{away?.name || "TBD"}</span>
        </div>
      </div>

      {hasInsight ? (
        <div className={`pbd-fixture-insight-wrap ${insightOpen ? "pbd-fixture-insight-open" : ""}`}>
          <button
            type="button"
            className="pbd-fixture-insight-toggle"
            aria-expanded={insightOpen}
            onClick={() => setInsightOpen((v) => !v)}
          >
            <span>Insights</span>
            <ChevronDown size={12} className="pbd-fixture-insight-chevron" aria-hidden />
          </button>
          {insightOpen ? <FixtureCardInsight fixture={fixture} /> : null}
        </div>
      ) : null}

      {!bettable ? (
        <div className="pbd-fixture-locked">
          <Lock size={12} />
          <span>Not open for new picks</span>
        </div>
      ) : (
        <>
          <div className="pbd-fixture-1x2">
            <QuickOddBtn
              label="1"
              pick={oneXTwo?.home}
              selected={isSameOddPick(selectedPick, oneXTwo?.home)}
              disabled={busy || pickBlocked}
              onPick={onQuickPick}
            />
            <QuickOddBtn
              label="X"
              pick={oneXTwo?.draw}
              selected={isSameOddPick(selectedPick, oneXTwo?.draw)}
              disabled={busy || pickBlocked}
              onPick={onQuickPick}
            />
            <QuickOddBtn
              label="2"
              pick={oneXTwo?.away}
              selected={isSameOddPick(selectedPick, oneXTwo?.away)}
              disabled={busy || pickBlocked}
              onPick={onQuickPick}
            />
          </div>

          {leg ? (
            <div className="pbd-fixture-picked">
              <strong>{formatOddPickLabel(selectedPick)}</strong>
              <em className="tabular-nums">@ {fmtOdd(selectedPick?.value)}</em>
            </div>
          ) : null}

          <div className="pbd-fixture-foot">
            <span>{mktsOffered} mkts</span>
            <span className="pbd-fixture-foot-dot" aria-hidden />
            <span>{bettorsCount} bettor{bettorsCount === 1 ? "" : "s"}</span>
          </div>

          <button
            type="button"
            className="pbd-fixture-markets"
            disabled={busy || marketCount === 0 || pickBlocked}
            onClick={() => onOpenMarkets?.(fixture)}
          >
            <Layers size={12} />
            <span>All markets</span>
            {marketCount > 0 ? <em>{marketCount}</em> : null}
          </button>
        </>
      )}
    </article>
  );
};

export default FixtureProgramCard;
